import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import Webcam from "react-webcam";
import { Camera, Upload, ArrowLeft, RefreshCw, Check, X } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const PhotoCapture = ({ onCapture, onSkip, onBack }) => {
  const [mode, setMode] = useState(null); // 'webcam' | 'upload'
  const [capturedImage, setCapturedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const videoConstraints = {
    width: 480,
    height: 480,
    facingMode: "user"
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage({ src: imageSrc, isBase64: true });
  }, [webcamRef]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload-photo`, formData);
      setCapturedImage({ src: response.data.photo_url, isBase64: false });
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!capturedImage) return;
    if (!capturedImage.isBase64) {
      // Already an S3 URL
      onCapture(capturedImage.src);
      return;
    }
    // Webcam base64 — upload to S3 first
    setIsUploading(true);
    try {
      const blob = dataURLtoBlob(capturedImage.src);
      const formData = new FormData();
      formData.append("file", blob, "webcam.jpg");
      const response = await axios.post(`${API}/upload-photo`, formData);
      onCapture(response.data.photo_url);
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  function dataURLtoBlob(dataURL) {
    const [header, data] = dataURL.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(data);
    const arr = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center relative px-6"
      data-testid="photo-capture"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-cyan/10 rounded-full blur-[120px]" />
      </div>

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={onBack}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-white transition-colors font-rajdhani"
        data-testid="photo-back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </motion.button>

      <div className="relative z-10 w-full max-w-lg mx-auto text-center">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wider">
            Capture Your Image
          </h2>
          <p className="font-rajdhani text-white/60">
            Optional: Add a photo for your superhero avatar
          </p>
        </motion.div>

        {/* Mode selection or capture view */}
        {!mode && !capturedImage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            <button
              onClick={() => setMode("webcam")}
              className="glass p-8 rounded-lg hover:border-neon-cyan/50 transition-all group"
              data-testid="webcam-mode-btn"
            >
              <Camera className="w-12 h-12 mx-auto mb-4 text-neon-cyan group-hover:scale-110 transition-transform" />
              <span className="font-rajdhani text-white font-semibold">Use Camera</span>
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="glass p-8 rounded-lg hover:border-neon-purple/50 transition-all group"
              data-testid="upload-mode-btn"
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-neon-purple group-hover:scale-110 transition-transform" />
              <span className="font-rajdhani text-white font-semibold">Upload Photo</span>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*"
              className="hidden"
              data-testid="file-input"
            />
          </motion.div>
        )}

        {/* Webcam view */}
        {mode === "webcam" && !capturedImage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative mb-8"
          >
            <div className="relative rounded-lg overflow-hidden hud-corners">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                className="w-full rounded-lg"
                data-testid="webcam"
              />
              {/* Scan line overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="scan-line" />
              </div>
              {/* HUD overlay corners */}
              <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-neon-cyan/50" />
              <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-neon-cyan/50" />
              <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-neon-cyan/50" />
              <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-neon-cyan/50" />
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setMode(null)}
                className="flex-1 py-3 bg-white/10 text-white font-rajdhani rounded-sm hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={capture}
                className="flex-1 py-3 bg-neon-cyan text-black font-orbitron tracking-wider rounded-sm hover:bg-neon-cyan/80 transition-colors flex items-center justify-center gap-2"
                data-testid="capture-btn"
              >
                <Camera className="w-5 h-5" />
                Capture
              </button>
            </div>
          </motion.div>
        )}

        {/* Preview captured image */}
        {capturedImage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8"
          >
            <div className="relative rounded-lg overflow-hidden neon-border-cyan">
              <img
                src={capturedImage.src}
                alt="Captured"
                className="w-full rounded-lg"
                data-testid="captured-image"
              />
              <div className="absolute top-2 right-2 bg-neon-green/20 text-neon-green px-3 py-1 rounded-full text-sm font-rajdhani flex items-center gap-1">
                <Check className="w-4 h-4" />
                Captured
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={handleRetake}
                className="flex-1 py-3 bg-white/10 text-white font-rajdhani rounded-sm hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
                data-testid="retake-btn"
              >
                <RefreshCw className="w-5 h-5" />
                Retake
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3 bg-neon-purple text-white font-orbitron tracking-wider rounded-sm hover:bg-neon-purple/80 transition-colors shadow-[0_0_20px_rgba(127,90,240,0.4)] flex items-center justify-center gap-2"
                data-testid="confirm-photo-btn"
              >
                <Check className="w-5 h-5" />
                Use Photo
              </button>
            </div>
          </motion.div>
        )}

        {/* Skip button */}
        {!capturedImage && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={onSkip}
            className="text-white/40 hover:text-white/60 transition-colors font-rajdhani underline underline-offset-4"
            data-testid="skip-photo-btn"
          >
            Skip this step
          </motion.button>
        )}

        {/* Loading overlay */}
        {isUploading && (
          <div className="fixed inset-0 z-50 bg-black/70 flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 border-2 border-neon-cyan/20 border-t-neon-cyan rounded-full animate-spin" />
            <p className="font-orbitron text-neon-cyan text-sm tracking-widest uppercase">Uploading photo...</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PhotoCapture;
